import type { RefObject } from "react";
import { DEFAULT_SCROLL_OFFSET_TOP } from "./scroll-controller-constants";
import type {
  QueuePendingTargetScrollInput,
  ScrollToRegisteredElementInput,
} from "./scroll-controller-types";

interface ScrollToRegisteredTargetInput extends ScrollToRegisteredElementInput {
  scrollRef: RefObject<HTMLDivElement | null>;
  queuePendingTargetScroll: (input: QueuePendingTargetScrollInput) => void;
  scrollToTopValue: (targetTop: number, instant: boolean) => void;
}

interface RegisteredScrollTarget {
  targetElement: HTMLElement;
}

export function scrollToRegisteredTarget({
  scrollRef,
  targetId,
  targetKind,
  targetElements,
  opts,
  queuePendingTargetScroll,
  scrollToTopValue,
}: ScrollToRegisteredTargetInput): void {
  const registeredTarget = getRegisteredScrollTarget({
    scrollRef,
    targetId,
    targetElements,
  });

  if (!registeredTarget) {
    queueMissingRegisteredTargetScroll({
      targetId,
      targetKind,
      opts,
      queuePendingTargetScroll,
    });
    return;
  }

  scrollToResolvedRegisteredTarget({
    registeredTarget,
    opts,
    scrollToTopValue,
  });
}

function queueMissingRegisteredTargetScroll({
  targetId,
  targetKind,
  opts,
  queuePendingTargetScroll,
}: Pick<
  ScrollToRegisteredTargetInput,
  "targetId" | "targetKind" | "opts" | "queuePendingTargetScroll"
>): void {
  queuePendingTargetScroll({ targetId, targetKind, opts });
}

function scrollToResolvedRegisteredTarget({
  registeredTarget,
  opts,
  scrollToTopValue,
}: Pick<ScrollToRegisteredTargetInput, "opts" | "scrollToTopValue"> & {
  registeredTarget: RegisteredScrollTarget;
}): void {
  const top = getRegisteredScrollTop({
    targetElement: registeredTarget.targetElement,
    offsetTop: opts?.offsetTop ?? DEFAULT_SCROLL_OFFSET_TOP,
  });
  scrollToTopValue(top, !!opts?.instant);
}

function getRegisteredScrollTarget({
  scrollRef,
  targetId,
  targetElements,
}: Pick<
  ScrollToRegisteredTargetInput,
  "scrollRef" | "targetId" | "targetElements"
>): RegisteredScrollTarget | null {
  const element = scrollRef.current;
  if (!element) return null;

  const targetElement = targetElements.current.get(targetId) ?? null;
  if (!targetElement) return null;

  return { targetElement };
}

export function getRegisteredScrollTop({
  targetElement,
  offsetTop,
}: {
  targetElement: HTMLElement;
  offsetTop: number;
}): number {
  return Math.max(0, targetElement.offsetTop - offsetTop);
}
